<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewBudgets' }" class="mb-0 main-identifier fw-semibold text-decoration-none">
        Budgets
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <router-link v-if="budgetId" :to="{ name: 'ViewBudget', params: { id: budgetId } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        View Budget
      </router-link>
      <span v-if="budgetId" class="material-symbols-outlined">keyboard_arrow_right</span>
      <router-link :to="{ name: 'ViewEliBudget', params: { id }, query: { budgetId: budgetId || undefined } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        View ELI Budget
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Edit ELI Budget</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer">
        <form @submit.prevent="handleSave">
          <div class="crud-form position-relative">

            <!-- White overlay while loading initial data -->
            <div v-if="loadingDetails" class="loading-overlay">
              <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
              <p class="mt-3 mb-0 text-muted">Loading ELI budget...</p>
            </div>

            <div class="form-inner">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">Edit ELI Budget</h1>
              </div>

              <div class="form-body">

                <div v-if="fetchError && !loadingDetails" class="alert alert-warning mt-3" role="alert">
                  {{ fetchError }}
                </div>

                <div class="row mt-2 mb-3">

                  <div class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                      <label class="form-label">MDA</label>
                    </div>
                    <div class="col-md-9 input-right">
                      <input type="text" class="form-control view-only-input"
                        :value="form.mdaName || 'N/A'" readonly />
                    </div>
                  </div>

                  <div class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                      <label class="form-label">Economic Line Item</label>
                    </div>
                    <div class="col-md-9 input-right">
                      <input type="text" class="form-control view-only-input"
                        :value="form.econLineItemName || 'N/A'" readonly />
                    </div>
                  </div>

                  <div class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                      <label class="form-label">Year</label>
                    </div>
                    <div class="col-md-9 input-right">
                      <input type="text" class="form-control view-only-input"
                        :value="form.year || 'N/A'" readonly />
                    </div>
                  </div>

                  <div class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                      <label for="amountBudgeted" class="form-label">
                        Amount Budgeted (₦) <span class="text-danger">*</span>
                      </label>
                    </div>
                    <div class="col-md-9 input-right">
                      <input id="amountBudgeted" type="text" class="form-control"
                        :value="formatCurrency(form.amountBudgeted)"
                        inputmode="numeric" pattern="[0-9,.]*"
                        @keypress="onlyDigitsAndComma"
                        @input="onAmountBudgetedInput"
                        :disabled="loadingSave"
                        required />
                    </div>
                  </div>

                  <div class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                      <label for="revenueAmount" class="form-label">
                        Revenue Amount (₦) <span class="text-danger">*</span>
                      </label>
                    </div>
                    <div class="col-md-9 input-right">
                      <input id="revenueAmount" type="text" class="form-control"
                        :value="formatCurrency(form.revenueAmount)"
                        inputmode="numeric" pattern="[0-9,.]*"
                        @keypress="onlyDigitsAndComma"
                        @input="onRevenueAmountInput"
                        :disabled="loadingSave"
                        required />
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="mt-4">
              <div class="d-flex justify-content-center gap-3">
                <button type="submit" class="btn btn-primary submit-btn" :disabled="loadingSave">
                  <span v-if="loadingSave" class="spinner-border spinner-border-sm me-2"
                    role="status" aria-hidden="true"></span>
                  {{ loadingSave ? 'Submitting...' : 'Submit' }}
                </button>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const id = route.params.id
const budgetId = route.query.budgetId || null

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const fetchError = ref('')
const loadingDetails = ref(false)
const loadingSave = ref(false)

const form = reactive({
  mdaName: '',
  econLineItemName: '',
  economicCode: '',
  orgCode: '',
  year: '',
  amountBudgeted: 0,
  revenueAmount: 0,
  amountReleased: 0,
  balance: 0,
  mdaId: '',
  economicLineItemId: '',
  version: null,
  createdDate: '',
  lastModifiedDate: '',
  createdBy: '',
  lastModBy: '',
  userId: '',
  title: '',
  performance: 0,
  budgetDate: '',
  totalRecAmtBudgeted: 0,
  totalCapAmtBudgeted: 0,
  totalRecAmtReleased: 0,
  totalCapAmtReleased: 0,
})

const fetchEliBudget = async () => {
  if (!id) {
    fetchError.value = 'No ELI budget ID provided.'
    return
  }
  fetchError.value = ''
  loadingDetails.value = true
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/eli/mda-eli-budget/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    form.mdaName = data.mdaName || ''
    form.econLineItemName = data.econLineItemName || ''
    form.economicCode = data.economicCode || ''
    form.orgCode = data.orgCode || ''
    form.year = data.year || ''
    form.amountBudgeted = data.amountBudgeted || 0
    form.revenueAmount = data.revenueAmount || 0
    form.amountReleased = data.amountReleased || 0
    form.balance = data.balance || 0
    form.mdaId = data.mdaId || ''
    form.economicLineItemId = data.economicLineItemId || ''
    form.version = data.version ?? null
    form.createdDate = data.createdDate || ''
    form.lastModifiedDate = data.lastModifiedDate || ''
    form.createdBy = data.createdBy || ''
    form.lastModBy = data.lastModBy || ''
    form.userId = data.userId || ''
    form.title = data.title || ''
    form.performance = data.performance || 0
    form.budgetDate = data.budgetDate || ''
    form.totalRecAmtBudgeted = data.totalRecAmtBudgeted || 0
    form.totalCapAmtBudgeted = data.totalCapAmtBudgeted || 0
    form.totalRecAmtReleased = data.totalRecAmtReleased || 0
    form.totalCapAmtReleased = data.totalCapAmtReleased || 0
  } catch (err) {
    console.error('Failed to fetch ELI budget:', err)
    fetchError.value = 'Failed to load ELI budget. Please try again.'
  } finally {
    loadingDetails.value = false
  }
}

const handleSave = async () => {
  const token = Cookies.get('auth_token')
  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  loadingSave.value = true

  try {
    await axios.put(`${root_api_url}/eli/mda-eli-budget/${id}`, form, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'user-id': userStore.uuid || '',
      },
    })

    Swal.fire({
      html: `${iconHtmlSuccess}
      <h1 style="font-weight:600;">Success</h1>
      <p>ELI Budget updated successfully.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })

    router.push({
      name: 'ViewEliBudget',
      params: { id },
      query: { budgetId: budgetId || undefined },
    })
  } catch (err) {
    console.error('Failed to update ELI budget:', err)

    if (err.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${err?.response?.data?.errorMessage || err?.response?.data?.error || 'Failed to update ELI Budget.'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred while updating ELI Budget. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    }
  } finally {
    loadingSave.value = false
  }
}

function formatCurrency(val) {
  if (val === '' || val === null || val === undefined) return ''
  const num = parseFloat(val).toFixed(2)
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function onlyDigitsAndComma(e) {
  const k = e.key
  if (
    (k >= '0' && k <= '9') ||
    k === ',' || k === '.' ||
    ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(k)
  ) return
  e.preventDefault()
}

const MAX_SAFE_RAW = Number.MAX_SAFE_INTEGER.toString()

function parseAmountInput(e) {
  let raw = e.target.value.replace(/\D/g, '')
  if (raw === '') return ''
  if (
    raw.length > MAX_SAFE_RAW.length ||
    (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)
  ) {
    raw = MAX_SAFE_RAW
  }
  return Number((parseInt(raw, 10) / 100).toFixed(2))
}

function onAmountBudgetedInput(e) {
  const val = parseAmountInput(e)
  if (val === '') {
    form.amountBudgeted = 0
    e.target.value = ''
    return
  }
  form.amountBudgeted = val
  e.target.value = formatCurrency(val)
}

function onRevenueAmountInput(e) {
  const val = parseAmountInput(e)
  if (val === '') {
    form.revenueAmount = 0
    e.target.value = ''
    return
  }
  form.revenueAmount = val
  e.target.value = formatCurrency(val)
}

onMounted(async () => {
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }
  await fetchEliBudget()
  document.title = 'Edit ELI Budget'
})
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: inherit;
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.submit-btn {
  background-color: #3498db !important;
  border-color: #3498db !important;
}

.submit-btn:hover {
  background-color: #2980b9 !important;
  border-color: #2980b9 !important;
}
</style>
